"use client";

import React, { useState, useMemo } from "react";
import Image from "next/image";
import SmoothImage from "@/utils/smoothImage";
import Link from "next/link";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import { Calendar } from "lucide-react";
import { useFetchNewsArticles } from "@/apis/queryFunctions/news";
import { dictionaries, Locale } from "@/utils/i18n";
import ScrollHeader from "@/components/Layouts/ScrollHeader";
import FooterContact from "@/components/Layouts/FooterContact";
import { PROJECT_INFO } from "@/types/projectConsts";
import { stripHTML, parseHtmlTextToJson } from "@/utils/stripHTML";

interface TinTucClientProps {
  homepageData: any;
  locale: string;
}

const formatDate = (dateString: string) => {
  if (!dateString) return "";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return dateString;
  }
};

export default function TinTucClient({
  homepageData,
  locale,
}: TinTucClientProps) {
  // Fetch live articles list from query
  const { data: newsItems = [], isLoading } = useFetchNewsArticles({
    condition: { ngon_ngu: locale },
  });

  const dict =
    dictionaries[locale as Locale]?.news_flow || dictionaries.vi.news_flow;

  // Flatten add-on list
  const allAddOns = [
    ...(Array.isArray(homepageData?.add_on_data)
      ? homepageData.add_on_data
      : []),
  ];

  const getAddOn = (key: string) => {
    return (
      allAddOns.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };

  const getAddOn2 = (key: string) => {
    return (
      homepageData?.add_on_data_2?.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };

  const getAddOn3 = (key: string) => {
    return (
      homepageData?.add_on_data_3?.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };

  // Dynamically configure global PROJECT_INFO properties from project_info JSON API data
  const projectInfo = homepageData?.content;
  if (projectInfo) {
    try {
      const cleanContent = stripHTML(projectInfo).trim();
      const parsed = JSON.parse(cleanContent);
      if (parsed.projectName) PROJECT_INFO.projectName = parsed.projectName;
      if (parsed.address) PROJECT_INFO.address = parsed.address;
      if (parsed.hotline) PROJECT_INFO.hotline = parsed.hotline;
      if (parsed.email) PROJECT_INFO.email = parsed.email;
      if (parsed.website) PROJECT_INFO.website = parsed.website;
      if (parsed.zalo) PROJECT_INFO.zalo = parsed.zalo;
      if (parsed.messenger) {
        PROJECT_INFO.messenger = parsed.messenger;
        PROJECT_INFO.fb = parsed.messenger;
      } else if (parsed.fb) {
        PROJECT_INFO.messenger = parsed.fb;
        PROJECT_INFO.fb = parsed.fb;
      }
    } catch (e) {
      console.error("Failed to parse project_info JSON:", e);
    }
  }

  const resolveTemplateStr = (str?: string) => {
    if (!str) return "";
    return str
      .replace("[PROJECT_INFO.hotline]", PROJECT_INFO.hotline)
      .replace("[PROJECT_INFO.email]", PROJECT_INFO.email)
      .replace("[PROJECT_INFO.website]", PROJECT_INFO.website);
  };

  // Header options mappings
  const heroItem = getAddOn("herosection");
  const heroFormAddon = homepageData?.add_on_data_2?.find(
    (item: any) => item?.title?.toLowerCase() === "hero-form",
  );
  let heroFormTitle = "ĐĂNG KÝ TƯ VẤN NGAY";
  let heroFormSubtitle = "Nhận thông tin mặt bằng & bảng giá mới nhất";
  let heroFormLayoutTypeTitle = "Sản phẩm quan tâm";
  let heroApptOptions: Array<{ label: string; value: string }> = [
    { label: "1PN", value: "1PN" },
    { label: "2PN", value: "2PN" },
    { label: "Studio", value: "Studio" },
  ];

  try {
    const rawContent = heroItem?.content
      ? stripHTML(heroItem.content).trim()
      : "";
    if (rawContent) {
      const parsed = JSON.parse(rawContent);
      if (parsed.heroFormTitle) heroFormTitle = parsed.heroFormTitle;
      if (parsed.heroFormSubtitle) heroFormSubtitle = parsed.heroFormSubtitle;
      if (parsed.heroFormLayoutTypeTitle)
        heroFormLayoutTypeTitle = parsed.heroFormLayoutTypeTitle;
      if (parsed.heroApptOptions && Array.isArray(parsed.heroApptOptions)) {
        heroApptOptions = parsed.heroApptOptions.map((item: any) => ({
          label: item.label || item.title || "Căn hộ",
          value: item.value || item.title || "Studio",
        }));
      }
    }
  } catch (e) {
    console.error("Failed to parse hero options:", e);
  }

  if (heroItem?.content_3) {
    const cleanContent3 = stripHTML(heroItem.content_3);
    const parts = cleanContent3.split("***").map((p) => p.trim());
    if (parts[0]) heroFormTitle = parts[0];
    if (parts[1]) heroFormSubtitle = parts[1];
  }

  // Footer data mapping
  const footerItem = getAddOn("footer");

  const partnersSection = getAddOn2("footer-partners");
  const partnersNames = partnersSection?.content
    ? stripHTML(partnersSection.content)
        .split("***")
        .map((s) => s.trim())
    : [];

  const partnersLogos = [
    partnersSection?.picture,
    partnersSection?.picture_2,
    partnersSection?.picture_3,
    partnersSection?.picture_4,
    partnersSection?.picture_5,
    partnersSection?.picture_6,
  ]
    .filter(Boolean)
    .map((src) => appendImageUrlFromAPI({ src, size: "s" }));

  const partnersList = partnersNames.map((name, idx) => ({
    name,
    logo: partnersLogos[idx] || "/logos/logo_with_name.png",
  }));

  const footerJsonData = stripHTML(footerItem?.content)
    ? parseHtmlTextToJson<any>(footerItem.content)
    : null;

  const footerData = (footerJsonData?.[0] || footerJsonData) as any;
  const footerInfoPart = footerData?.footerInfo;

  const FOOTERINFO = {
    title: footerInfoPart?.title || "Liên hệ tư vấn",
    description1:
      footerInfoPart?.description1 ||
      "Dragon Home – Căn hộ chung cư hiện đại chuẩn sống mới tại Long An.",
    title2: footerInfoPart?.title2 || "Địa chỉ",
    description2:
      footerInfoPart?.description2 ||
      "Khu dân cư Ngọc Long, Đức Hòa Đông, Đức Hòa, Long An, nay là Mỹ Hạnh, Tây Ninh.",
    title3: footerInfoPart?.title3 || "liên hệ",
    description3:
      footerInfoPart?.description3 ||
      "Quý khách hàng vui lòng để lại thông tin, Dragon Home sẽ liên hệ và hỗ trợ trong thời gian sớm nhất.",
    phone:
      resolveTemplateStr(footerInfoPart?.phone) ||
      `Hotline: ${PROJECT_INFO.hotline}`,
    mail:
      resolveTemplateStr(footerInfoPart?.mail) ||
      `Email: ${PROJECT_INFO.email}`,
    webUrl:
      resolveTemplateStr(footerInfoPart?.webUrl) ||
      `Website: ${PROJECT_INFO.website}`,
    fb: footerInfoPart?.fb || "https://www.facebook.com/orapark/",
    youtube:
      footerInfoPart?.youtube ||
      "https://www.youtube.com/@OneVisionGroup-VietNam",
  };

  const footerReasons = footerData?.footerReasons;

  const footerRegisterPart = footerData?.footerRegister;
  const footerRegister = {
    title: footerRegisterPart?.title || "ĐĂNG KÝ NHẬN THÔNG TIN ƯU TIÊN",
    subtitle: footerRegisterPart?.subtitle || "",
    benefits: footerRegisterPart?.benefits || [],
    calloutTitle:
      footerRegisterPart?.calloutTitle || "NHẬN THÔNG TIN NGAY HÔM NAY",
    calloutDesc:
      footerRegisterPart?.calloutDesc ||
      "Booking chỉ 20 triệu • Giá dự kiến từ 25 triệu/m² • Thanh toán đợt đầu chỉ 20%",
    hotline:
      resolveTemplateStr(footerRegisterPart?.hotline) ||
      `Hotline: ${PROJECT_INFO.hotline}`,
    note:
      footerRegisterPart?.note ||
      "Đăng ký ngay để trở thành những khách hàng đầu tiên lựa chọn vị trí đẹp và chính sách tốt nhất của Dragon Home.",
  };

  const footerBgImage =
    appendImageUrlFromAPI({ src: footerItem?.picture, size: "xl" }) ||
    "/images/phoi_canh_3.png";

  const footerPartnersLabel = footerData?.partnersLabel || "ĐỐI TÁC DỰ ÁN";
  const footerAddressLabel = footerInfoPart?.title2 || "ĐỊA CHỈ DỰ ÁN";
  const footerContactLabel = footerInfoPart?.title3 || "THÔNG TIN LIÊN HỆ";
  const footerReasonsTitle = footerData?.reasonsTitle || "ĐẶC QUYỀN SỞ HỮU";
  const footerReasonsSubtitle =
    footerData?.reasonsSubtitle || "VÌ SAO NÊN XUỐNG TIỀN NGAY HÔM NAY?";
  const footerBenefitsTitle =
    footerRegisterPart?.benefitsTitle ||
    footerData?.benefitsTitle ||
    "Đăng ký ngay hôm nay để nhận:";
  const footerCopyrightText =
    footerData?.copyrightText ||
    footerData?.copyright ||
    "©2026 Copyright by ZenGroup. All rights reserved.";

  return (
    <>
      <ScrollHeader
        fb={FOOTERINFO.fb}
        mail={FOOTERINFO.mail}
        apptOptions={heroApptOptions}
        formTitle={heroFormTitle}
        formSubtitle={heroFormSubtitle}
        formLayoutTypeTitle={heroFormLayoutTypeTitle}
      />

      <main className="min-h-screen bg-[#FBF9F5] pt-[80px] md:pt-[100px]">
        {/* Banner Section */}
        <section className="relative py-20 md:py-28 text-white text-center overflow-hidden border-b border-black/5 bg-primaryDark">
          {/* Subtle slow glowing background effect */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at center, #31592F 0%, #214427 100%)",
            }}
          />
          {/* Decorative pattern lines */}
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
          <div className="relative z-10 max-w-4xl mx-auto px-6 space-y-4">
            <h1 className="text-3xl md:text-5xl font-black uppercase tracking-wider text-secondary-hover font-montserrat">
              {dict.blog_title || "TIN TỨC & SỰ KIỆN"}
            </h1>
            <div className="w-[80px] h-[2px] bg-gradient-to-r from-transparent via-[#B59A7A] to-transparent mx-auto my-3" />
            <p className="text-xs sm:text-sm md:text-base text-white/70 font-medium max-w-2xl mx-auto font-montserrat leading-relaxed">
              {dict.blog_desc ||
                "Cập nhật các thông tin, sự kiện và tiến độ dự án mới nhất"}
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 max-w-7xl mx-auto px-6 md:px-8">
          {isLoading ? (
            <div className="flex justify-center items-center py-24">
              <div className="w-12 h-12 border-4 border-[#B59A7A] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : newsItems.length === 0 ? (
            <div className="text-center py-16 text-primary/65 font-semibold text-sm">
              {dict.no_results || "Không tìm thấy bài viết nào."}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {newsItems.map((item: any) => {
                const imageUrl = appendImageUrlFromAPI({
                  src: item.picture || item.image,
                  size: "xl",
                });
                return (
                  <Link
                    key={item._id || item.id}
                    href={`/${locale}/tin-tuc/${item.slug}`}
                    className="flex flex-col bg-white rounded-2xl overflow-hidden border border-black/5 hover:border-[#B59A7A]/30 hover:shadow-[0_8px_40px_rgba(181,154,122,0.15)] transition-all duration-300 cursor-pointer group h-full"
                  >
                    {/* Cover image */}
                    <div className="relative aspect-[16/10] bg-gray-50 overflow-hidden">
                      <SmoothImage
                        src={imageUrl || "/images/profession1.png"}
                        alt={item.title}
                        fill
                        sizes="(min-width: 1024px) 30vw, (min-width: 768px) 50vw, 100vw"
                        className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    </div>

                    {/* Metadata Content */}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-1.5 text-gray-400 text-[11px] font-bold mb-3 uppercase tracking-wider">
                        <Calendar size={12} className="text-[#B59A7A]" />
                        <span>
                          {formatDate(item.date_created || item.date)}
                        </span>
                      </div>
                      <h3 className="text-sm sm:text-base font-extrabold text-primary group-hover:text-[#B59A7A] transition duration-200 line-clamp-2 leading-snug mb-3 font-montserrat">
                        {item.title}
                      </h3>
                      <p className="text-xs text-gray-500 line-clamp-3 leading-relaxed flex-1 font-montserrat">
                        {item.summary ||
                          (item.content
                            ? item.content
                                .replace(/<[^>]*>/g, "")
                                .substring(0, 120) + "..."
                            : "")}
                      </p>
                      <span className="text-[#B59A7A] font-bold text-xs uppercase tracking-wider mt-4 inline-block group-hover:translate-x-1.5 transition-transform duration-300">
                        {dict.read_more || "Đọc chi tiết"} →
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <div id="dang-ky-bao-gia">
        <FooterContact
          INFO={FOOTERINFO}
          reasonsList={footerReasons}
          registerInfo={footerRegister}
          bgImageUrl={footerBgImage}
          apptOptions={heroApptOptions}
          formTitle={heroFormTitle}
          formSubtitle={heroFormSubtitle}
          partners={partnersList}
          partnersLabel={footerPartnersLabel}
          addressLabel={footerAddressLabel}
          contactLabel={footerContactLabel}
          reasonsTitle={footerReasonsTitle}
          reasonsSubtitle={footerReasonsSubtitle}
          benefitsTitle={footerBenefitsTitle}
          copyrightText={footerCopyrightText}
          logoUrl={
            appendImageUrlFromAPI({ src: footerItem?.picture_2, size: "xl" }) ||
            "/logos/logo_transparent.png"
          }
        />
      </div>
    </>
  );
}
